import { useInfiniteQuery, useMutation, type InfiniteData } from '@tanstack/react-query'

import { createQueryKeys, queryClient, type ApiError } from '../core'
import type { ConversationMessageDto, ConversationPageDto, PostCommentBody } from './conversations.schemas'
import { conversationsService } from './conversations.service'

export const conversationsKeys = createQueryKeys('conversations')

export const COMMENTS_PAGE_SIZE = 50

type Cache = InfiniteData<ConversationPageDto, number>

const keyOf = (projectId: string) => conversationsKeys.all({ projectId })
const time = (message: ConversationMessageDto) => Date.parse(message.createdAt ?? '') || 0

/**
 * `useProjectComments(projectId)`: the project's comments, latest first. The server pages them (50 at a time); `fetchNextPage` brings in
 * the older ones. Pages are joined, a comment that moved from one page to the next while others were posted is shown once, and the
 * whole list is sorted by time here, so the order never depends on how the server happens to sort.
 */
export function useProjectComments(projectId: string | undefined) {
  return useInfiniteQuery({
    queryKey: keyOf(projectId ?? ''),
    enabled: Boolean(projectId),
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) => conversationsService.list(projectId!, { page: pageParam, pageSize: COMMENTS_PAGE_SIZE }, signal),
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((count, page) => count + page.messages.length, 0)
      return last.messages.length > 0 && loaded < last.totalMessages ? pages.length + 1 : undefined
    },
    staleTime: 15_000,
    // Other people comment while this one is open, so coming back to the window looks for new ones.
    refetchOnWindowFocus: true,
    select: (data) => {
      const byId = new Map(data.pages.flatMap((page) => page.messages).map((message) => [message.id, message]))
      const total = data.pages[0]?.totalMessages ?? 0
      const messages = [...byId.values()].toSorted((a, b) => time(b) - time(a))
      return { messages, total: Math.max(total, messages.length) }
    },
  })
}

/**
 * `usePostComment(projectId).mutateAsync({ createdBy, message })`: adds a comment. The server's answer is put at the top of the list that is
 * already on screen, so it appears at once without asking for the list again. Never retried by itself.
 */
export function usePostComment(projectId: string) {
  return useMutation<ConversationMessageDto, ApiError, PostCommentBody>({
    mutationFn: (body) => conversationsService.post(projectId, body),
    onSuccess: (created) => {
      const cached = queryClient.getQueryData<Cache>(keyOf(projectId))
      if (!cached?.pages.length) return void queryClient.invalidateQueries({ queryKey: keyOf(projectId) })
      const [first, ...rest] = cached.pages
      queryClient.setQueryData<Cache>(keyOf(projectId), { ...cached, pages: [{ ...first!, totalMessages: first!.totalMessages + 1, messages: [created, ...first!.messages] }, ...rest] })
    },
  })
}
